import { createSlice } from '@reduxjs/toolkit';
import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
// utils
import axios from '../../utils/axios';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  products: [],
  categories: [],
  fabrics: [],
  product: null,
  sortBy: null,
  filters: {
    gender: [],
    category: 'All',
    colors: [],
    priceRange: '',
    rating: '',
    createdAt: '',
  },
  checkout: {
    activeStep: 0,
    cart: [],
    subtotal: 0,
    total: 0,
    discount: 0,
    shipping: 0,
    billing: null,
  },
  totalItems: 0,
  ItemsOnPage: 0
};

const slice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET PRODUCTS
    getProductsSuccess(state, action) {
      state.isLoading = false;
      state.products = action.payload.products;
      state.totalItems = action.payload.totalProducts;
      state.ItemsOnPage = action.payload.totalProducts.length;
    },

    getCategorySuccess(state, action) {
      state.isLoading = false;
      state.categories = action.payload;
    },

    getFabricSuccess(state, action) {
      state.isLoading = false;
      state.fabrics = action.payload;
    },

    // GET PRODUCT
    getProductSuccess(state, action) {
      state.isLoading = false;
      state.product = action.payload;
    },

    // CLEAR PRODUCT
    clearProduct(state) {
      state.product = null;
    },

    addProductSuccess(state, action) {
      const newProduct = action.payload; 
      state.isLoading = false;
    },

    addProductFeatureSuccess(state, action) {
      const newFeature = action.payload; 
      state.isLoading = false;
    },

    addProductGroupSuccess(state, action) {
      const newGroup= action.payload; 
      state.isLoading = false;
    },

    //  SORT & FILTER PRODUCTS
    sortByProducts(state, action) {
      state.sortBy = action.payload;
    },

    filterProducts(state, action) {
      state.filters.gender = action.payload.gender;
      state.filters.category = action.payload.category;
      state.filters.colors = action.payload.colors;
      state.filters.priceRange = action.payload.priceRange;
      state.filters.rating = action.payload.rating;
      state.filters.createdAt = action.payload.createdAt;
    },

    // CHECKOUT
    getCart(state, action) {
      const cart = action.payload;

      const subtotal = sum(cart.map((cartItem) => cartItem.price * cartItem.quantity));
      const discount = cart.length === 0 ? 0 : state.checkout.discount;
      const shipping = cart.length === 0 ? 0 : state.checkout.shipping;
      const billing = cart.length === 0 ? null : state.checkout.billing;

      state.checkout.cart = cart;
      state.checkout.discount = discount;
      state.checkout.shipping = shipping;
      state.checkout.billing = billing;
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal - discount;
    },

    addCart(state, action) {
      const product = action.payload;
      const isEmptyCart = state.checkout.cart.length === 0;

      if (isEmptyCart) {
        state.checkout.cart = [...state.checkout.cart, product];
      } else {
        state.checkout.cart = state.checkout.cart.map((_product) => {
          const isExisted = _product.id === product.id;
          if (isExisted) {
            return {
              ..._product,
              quantity: _product.quantity + 1,
            };
          }
          return _product;
        });
      }
      state.checkout.cart = uniqBy([...state.checkout.cart, product], 'id');
    },

    deleteCart(state, action) {
      const updateCart = state.checkout.cart.filter((item) => item.id !== action.payload);

      state.checkout.cart = updateCart;
    },

    resetCart(state) {
      state.checkout.activeStep = 0;
      state.checkout.cart = [];
      state.checkout.total = 0;
      state.checkout.subtotal = 0;
      state.checkout.discount = 0;
      state.checkout.shipping = 0;
      state.checkout.billing = null;
    },

    onBackStep(state) {
      state.checkout.activeStep -= 1;
    },

    onNextStep(state) {
      state.checkout.activeStep += 1;
    },

    onGotoStep(state, action) {
      const goToStep = action.payload;
      state.checkout.activeStep = goToStep;
    },

    increaseQuantity(state, action) {
      const productId = action.payload;
      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            quantity: product.quantity + 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    decreaseQuantity(state, action) {
      const productId = action.payload;
      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            quantity: product.quantity - 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    createBilling(state, action) {
      state.checkout.billing = action.payload;
    },

    applyDiscount(state, action) {
      const discount = action.payload;
      state.checkout.discount = discount;
      state.checkout.total = state.checkout.subtotal - discount;
    },

    applyShipping(state, action) {
      const shipping = action.payload;
      state.checkout.shipping = shipping;
      state.checkout.total = state.checkout.subtotal - state.checkout.discount + shipping;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  getCart,
  addCart,
  resetCart,
  onGotoStep,
  onBackStep,
  onNextStep,
  deleteCart,
  createBilling,
  applyShipping,
  applyDiscount,
  increaseQuantity,
  decreaseQuantity,
  sortByProducts,
  filterProducts,
  clearProduct,
} = slice.actions;

// ----------------------------------------------------------------------

export function getProducts(data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/api/productV/pagination/all', {
        params: {
          ...data
        }
      });
      dispatch(slice.actions.getProductsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCategory() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/api/category');
      dispatch(slice.actions.getCategorySuccess(response.data));
      console.log(response.data,'response')
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getFabric() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/api/fabric');
      dispatch(slice.actions.getFabricSuccess(response.data));
      console.log(response.data,'response')
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}


export function addProduct(newProduct) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/api/productV/add', newProduct);
      dispatch(slice.actions.addProductSuccess(response.data.event));
      return { payload: {} };
    } catch (error) {
      console.log('error')
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'An error occurred';
      dispatch(slice.actions.hasError(error));
      return { payload: { error: errorMessage } };
    }
  };
}
export function duplicateProduct(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/api/productV/duplicate/${id}`);
      dispatch(slice.actions.addProductSuccess(response.data.event));
      return { payload: {} };
    } catch (error) {
      console.log('error')
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'An error occurred';
      dispatch(slice.actions.hasError(error));
      return { payload: { error: errorMessage } };
    }
  };
}

export function updateProduct(id,newProduct) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put(`/api/productV/update/${id}`, newProduct);
      dispatch(slice.actions.addProductSuccess(response.data.event));
      return { payload: {} };
    } catch (error) {
      console.log('error')
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'An error occurred';
      dispatch(slice.actions.hasError(error));
      return { payload: { error: errorMessage } };
    }
  };
}


export function addProductFeature(newFeature) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/api/feature/add', newFeature);
      dispatch(slice.actions.addProductFeatureSuccess(response.data.event));
      return { payload: {} };
    } catch (error) {
      console.log('error')
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'An error occurred';
      dispatch(slice.actions.hasError(error));
      return { payload: { error: errorMessage } };
    }
  };
}

// export function addProductGrouping(newGroup) {
//   return async (dispatch) => {
//     dispatch(slice.actions.startLoading());
//     try {
//       const response = await axios.post('/api/product-group/add ', newGroup);
//       dispatch(slice.actions.addProductGroupSuccess(response.data.event));
//       return { payload: {} };
//     } catch (error) {
//       console.log('error')
//       dispatch(slice.actions.hasError(error));
//       return { payload: { error: 'Error' } };
//     }
//   };
// }



// ----------------------------------------------------------------------

export function getProduct(name) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/api/products/product', {
        params: { name },
      });
      dispatch(slice.actions.getProductSuccess(response.data.product));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getProductById(id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/api/productV/${id}`);
      dispatch(slice.actions.getProductSuccess(response.data));
      return response.data || null;
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}


export function deleteProductById(id) {
  return async () => {
    // dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put(`/api/productV/delete/${id}`);
      // dispatch(slice.actions.getProductSuccess(response.data));
      // getProducts();
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function appendProducts(data) {
  return async () => {
    try {
      const response = await axios.get('/api/productV/pagination/all', {
        params: {
          ...data
        }
      });
      // Return products, total count, and page for local use
      return {
        products: response.data.products,
        totalProducts: response.data.totalProducts,
        totalPages: response.data.totalPages,
        page: data.page || 1
      };
    } catch (error) {
      return { products: [], totalProducts: 0, totalPages: 1, page: data.page || 1, error };
    }
  };
}